/**
 * Account panel contents (opened from the name top right — AppShell `profile`): who is signed in,
 * notification controls, and change password.
 *
 * Notifications have two independent switches. "This computer": on asks the browser for
 * permission while it still may (once the site is blocked, browsers refuse to ask again, so the
 * panel explains how to allow it in the site settings and notices when that happens); off
 * unregisters this browser. "My account": the profile's `push_master_kill`, for every device.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { describeError } from "../api/errors";
import { getMe, patchProfile } from "../api/patient";
import {
  PUSH_RETRY_EVENT,
  PUSH_STATUS_EVENT,
  pushOnHere,
  pushPermission,
  pushSetupError,
  setPushOnHere,
  watchPushPermission,
} from "../lib/push";
import type { PushPermission } from "../lib/push";
import { phoneLabel } from "../lib/phone";
import { ChangePasswordCard } from "./ChangePasswordCard";

export function AccountPanel() {
  const qc = useQueryClient();
  const { data: me, isError } = useQuery({ queryKey: ["me"], queryFn: getMe });
  if (isError) return <p className="text-sm text-red-600">Couldn't load your profile.</p>;
  if (!me) return <p className="text-sm text-ink-muted">Loading…</p>;

  return (
    <>
      <section className="rounded-xl border border-line p-4">
        <p className="text-base font-semibold text-ink">{me.full_name}</p>
        <p className="text-sm capitalize text-ink-soft">{me.role}</p>
        <dl className="mt-3 space-y-2 text-sm">
          <Detail label="Phone" value={me.phone ? phoneLabel(me.phone) : "—"} />
          <Detail label="Email" value={me.email ?? "—"} />
        </dl>
      </section>

      <NotificationSettings pushOff={me.push_master_kill} />

      {/* A first password flips the card to "change"; without a refresh a second try would get
          PASSWORD_ALREADY_SET. */}
      <ChangePasswordCard
        passwordSet={me.password_set}
        collapsible
        onPasswordSet={() => void qc.invalidateQueries({ queryKey: ["me"] })}
      />
    </>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-ink-muted">{label}</dt>
      <dd className="truncate font-medium text-ink">{value}</dd>
    </div>
  );
}

function NotificationSettings({ pushOff }: { pushOff: boolean }) {
  const qc = useQueryClient();
  const [permission, setPermission] = useState<PushPermission | null>(null);
  const [onHere, setOnHere] = useState(pushOnHere);
  const [switching, setSwitching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [setupError, setSetupErrorState] = useState(pushSetupError);

  const recheck = () => void pushPermission().then(setPermission);
  useEffect(() => {
    recheck();
    return watchPushPermission(setPermission);
  }, []);
  useEffect(() => {
    const update = () => setSetupErrorState(pushSetupError());
    window.addEventListener(PUSH_STATUS_EVENT, update);
    return () => window.removeEventListener(PUSH_STATUS_EVENT, update);
  }, []);

  const toggleAccount = useMutation({
    mutationFn: (on: boolean) => patchProfile({ push_master_kill: !on }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["me"] }),
    onError: (e) => setError(describeError(e)),
  });

  const switchHere = async (on: boolean) => {
    setSwitching(true);
    setError(null);
    try {
      setPermission(await setPushOnHere(on));
      setOnHere(on);
    } catch (e) {
      setError(describeError(e));
    } finally {
      setSwitching(false);
    }
  };

  const usable = permission !== "unsupported" && permission !== "unconfigured" && permission !== null;
  const blocked = permission === "denied";

  return (
    <section className="rounded-xl border border-line p-4">
      <h3 className="text-sm font-semibold text-ink">Notifications</h3>

      <Toggle
        title="This computer"
        hint={
          !usable
            ? permission === "unconfigured"
              ? "Notifications aren't set up for this site."
              : "This browser can't show notifications."
            : "New orders and updates pop up while you work in this browser."
        }
        state={
          <BrowserState
            permission={usable && !onHere ? "default" : permission}
            failed={permission === "granted" && onHere && setupError !== null}
          />
        }
        checked={usable && !blocked && onHere && permission === "granted"}
        disabled={!usable || blocked || switching}
        onChange={(on) => void switchHere(on)}
      />
      {permission === "granted" && onHere && setupError && (
        <div className="mt-2 rounded-lg bg-amber-50 p-3 text-xs text-amber-900">
          <p className="font-medium">Allowed, but this browser isn't connected.</p>
          <p className="mt-1">{setupError}</p>
          <button
            onClick={() => window.dispatchEvent(new Event(PUSH_RETRY_EVENT))}
            className="mt-2 font-medium text-amber-900 underline"
          >
            Try again
          </button>
        </div>
      )}
      {blocked && (
        <div className="mt-2 rounded-lg bg-amber-50 p-3 text-xs text-amber-900">
          <p className="font-medium">Notifications are blocked for this site in your browser.</p>
          <p className="mt-1">Browsers don't let a site ask again once blocked. To turn them on:</p>
          <ol className="mt-1 list-decimal space-y-0.5 pl-4">
            <li>Click the site icon to the left of the address bar.</li>
            <li>
              Set <span className="font-medium">Notifications</span> to <span className="font-medium">Allow</span>.
            </li>
          </ol>
          <p className="mt-1">This panel picks the change up by itself.</p>
          <button onClick={recheck} className="mt-2 font-medium text-amber-900 underline">
            Check again
          </button>
        </div>
      )}

      <Toggle
        title="My account (all devices)"
        hint="Off stops push notifications on every device you sign in on."
        checked={!pushOff}
        disabled={toggleAccount.isPending}
        onChange={(on) => {
          setError(null);
          toggleAccount.mutate(on);
        }}
      />
      {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
    </section>
  );
}

function Toggle({
  title,
  hint,
  state,
  checked,
  disabled,
  onChange,
}: {
  title: string;
  hint: string;
  state?: React.ReactNode;
  checked: boolean;
  disabled: boolean;
  onChange: (on: boolean) => void;
}) {
  return (
    <label className="mt-3 flex items-start justify-between gap-3 border-t border-line pt-3 text-sm first-of-type:border-t-0">
      <span className="min-w-0">
        <span className="flex items-center gap-2 text-ink">
          {title}
          {state}
        </span>
        <span className="block text-xs text-ink-muted">{hint}</span>
      </span>
      <input
        type="checkbox"
        role="switch"
        aria-label={title}
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1 h-5 w-5 shrink-0 accent-brand disabled:opacity-50"
      />
    </label>
  );
}

function BrowserState({ permission, failed = false }: { permission: PushPermission | null; failed?: boolean }) {
  const [label, tone] =
    failed
      ? ["Not connected", "bg-amber-50 text-amber-800"]
      : permission === "granted"
      ? ["On", "bg-emerald-50 text-emerald-700"]
      : permission === "denied"
        ? ["Blocked", "bg-red-50 text-red-700"]
        : permission === "default"
          ? ["Off", "bg-gray-100 text-gray-600"]
          : permission === "unsupported"
            ? ["Not supported", "bg-gray-100 text-gray-600"]
            : permission === "unconfigured"
              ? ["Not set up", "bg-gray-100 text-gray-600"]
              : ["…", "bg-gray-100 text-gray-600"];
  return <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${tone}`}>{label}</span>;
}
